import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { MoonLoader } from 'react-spinners';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/20/solid';

import config from '@project/config';
import { Button, Card } from '@project/components/atoms';
import { magazineService } from '@project/services';
import { TOAST_DURATION } from '@project/helper';
import { MagazineCheckoutForm } from './magazine-checkout.form';

const stripe = loadStripe(config.stripePublishableKey);

export const MagazineSubscribePage: React.FC = () => {
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    magazineService
      .subscribe()
      .then((response: { client_secret?: string }) => {
        if (!isMounted) return;

        if (response?.client_secret) {
          setClientSecret(response.client_secret);
        } else {
          toast.error('Could not start subscription', TOAST_DURATION);
          navigate('/partner/magazines');
        }
      })
      .catch((error: { response?: { data?: { error?: string } } }) => {
        if (!isMounted) return;
        toast.error(error?.response?.data?.error ?? 'Could not start subscription', TOAST_DURATION);
        navigate('/partner/magazines');
      })
      .finally(() => isMounted && setIsLoading(false));

    return () => {
      isMounted = false;
    };
  }, []);

  if (isLoading || !clientSecret) {
    return (
      <div className="mt-40 flex justify-center">
        <MoonLoader color="#B58B00" />
      </div>
    );
  }

  return (
    <div className="mx-auto mt-10 max-w-xl px-4">
      <div className="mb-6 flex items-center gap-3">
        <Button onClick={() => navigate(-1)} variant="text">
          <ArrowLeftIcon className="w-5 text-black" />
        </Button>
        <p className="text-2xl font-semibold">Magazine Subscription</p>
      </div>

      <Card className="p-6">
        <div className="mb-6 border-b pb-4">
          <p className="font-semibold">Monthly Magazine Access</p>
          <p className="text-sm text-gray-500">
            Unlock all paid magazines for{' '}
            <span className="font-semibold text-gold">$20 / month</span>. Cancel anytime from Manage
            Subscriptions.
          </p>
        </div>

        <Elements stripe={stripe} options={{ clientSecret }}>
          <MagazineCheckoutForm />
        </Elements>
      </Card>
    </div>
  );
};
